import { type ProColumns } from '@ant-design/pro-components'
import { Link } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { type ServiceOrder } from '@super-mega/server/contracts/order'
import { TableRowActions, type TableRowAction } from '@/components/table-row-actions.tsx'
import { TABLE_ACTION_COLUMN } from '@/const/list.ts'
import type { useMemberCreatorOptions } from '@/features/access/index.ts'
import { OrderContractCountCell } from '@/features/contract/index.ts'
import { BusinessTag } from '@/features/master-data/index.ts'
import { ORDER_STATUS } from './const/index-list.ts'
import type { useOrderListFilterOptions } from './hooks/use-order-list-filter-options.ts'
import { OrderSubjectCell } from './order-list-cells.tsx'
import {
  createOrderListAuditColumns,
  createOrderListBaseColumns,
  ORDER_LIST_COLUMN_WIDTHS
} from './order-list-columns.ts'
import { type OrderListSearch } from './order-list-search.ts'

type ServiceOrderRecord = ServiceOrder

type OrderSearch = OrderListSearch

export function createServiceColumns({
  isSuperAdmin,
  canManage,
  canSubmit,
  onDelete,
  onForceDelete,
  canReadBills,
  onView,
  onBills,
  onContracts,
  busy,
  onOpen,
  search,
  memberCreator,
  filterOptions
}: {
  isSuperAdmin: boolean
  search: OrderSearch
  memberCreator: ReturnType<typeof useMemberCreatorOptions>
  canManage: boolean
  canSubmit: boolean
  busy: boolean
  canReadBills: boolean
  onView: (orderId: number) => void
  onContracts: (order: ServiceOrderRecord) => void
  onBills: (order: ServiceOrderRecord) => void
  onDelete: (order: ServiceOrderRecord) => void
  onForceDelete: (order: ServiceOrderRecord) => void
  onOpen: (orderId: number) => void
  filterOptions: ReturnType<typeof useOrderListFilterOptions>
}): ProColumns<ServiceOrderRecord>[] {
  return [
    ...createOrderListBaseColumns<ServiceOrderRecord>({
      keywordPlaceholder: '订单号、ID 或备注',
      filterOptions,
      statusMap: ORDER_STATUS,
      renderOrderNumber: (order) => (
        <Link
          className="text-content! hover:text-accent! font-semibold underline! underline-offset-2 transition-colors"
          title="在新标签页查看订单详情"
          to="/orders/service/$orderId"
          params={{ orderId: String(order.id) }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {order.orderNo}
        </Link>
      ),
      renderOrderGroup: (order) => {
        if (!order.orderGroupId) return undefined
        return (
          <Link
            className="hover:text-accent! text-inherit! underline! underline-offset-2 transition-colors"
            title="在新标签页查看订单组"
            to="/orders/groups/$groupId"
            params={{ groupId: String(order.orderGroupId) }}
            search={{ kind: 'service' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {order.orderGroupName || order.orderGroupId}
          </Link>
        )
      },
      renderCustomer: (order) => (
        <Link
          className="text-content! hover:text-accent! underline! underline-offset-2 transition-colors"
          title="在新标签页查看客户资料"
          to="/customer-management/customers/$customerId/documents"
          params={{ customerId: String(order.customerId) }}
          search={{ kind: 'service' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {order.customerName || '—'}
        </Link>
      ),
      renderSubject: (order) => (
        <OrderSubjectCell
          primary={order.companyName || order.subjectName}
          secondary={
            order.identityName ? `${order.subjectKind === 'person' ? '身份' : '法人'}：${order.identityName}` : null
          }
          secondaryFallback={order.subjectKind === 'person' ? '未关联身份' : '未关联法人'}
        />
      )
    }),
    {
      dataIndex: 'businessName',
      ellipsis: true,
      search: false,
      title: '业务类型',
      width: ORDER_LIST_COLUMN_WIDTHS.businessType,
      render: (_, order) => <BusinessTag businessColor={order.businessColor} businessName={order.businessName} />
    },
    {
      title: '服务模式',
      dataIndex: 'serviceMode',
      valueType: 'select',
      valueEnum: { once: { text: '一次性服务' }, term: { text: '期限服务' } },
      width: '7rem'
    },
    {
      title: '服务期限',
      dataIndex: 'startAt',
      search: false,
      width: '13rem',
      render: (_, order) =>
        order.startAt && order.endAt
          ? `${dayjs.utc(order.startAt).format('YYYY-MM-DD')} 至 ${dayjs.utc(order.endAt).format('YYYY-MM-DD')}`
          : '—'
    },
    {
      title: '合同文件',
      dataIndex: 'contractDocumentCount',
      search: false,
      width: '8rem',
      render: (_, order) => (
        <OrderContractCountCell order={{ ...order, type: 'service' }} onOpen={() => onContracts(order)} />
      )
    },
    ...createOrderListAuditColumns<ServiceOrderRecord>({ search, memberCreator }),
    {
      ...TABLE_ACTION_COLUMN,
      render: (_, order) => {
        const actions: TableRowAction[] = [{ key: 'view', label: '查看', onClick: () => onView(order.id) }]
        const canEditContent = order.editRestriction === null && canSubmit
        if (canEditContent || canManage) {
          actions.push({
            key: 'edit',
            label: '编辑订单',
            onClick: () => onOpen(order.id),
            disabled: busy
          })
        }
        actions.push({ key: 'contracts', label: '查看合同文件', onClick: () => onContracts(order) })
        actions.push({
          key: 'contract',
          label: '打印合同',
          onClick: () => {
            window.open(`/orders/service/contract/${order.id}`, '_blank', 'noopener,noreferrer')
          }
        })

        if (canReadBills) {
          actions.push({ key: 'bills', label: '查看账单', onClick: () => onBills(order) })
        }
        if (canManage) {
          const canDelete =
            isSuperAdmin || order.status === 'pending' || !!order.archivedAt || order.status === 'archived'
          actions.push({
            key: 'delete',
            label: '删除订单',
            danger: true,
            onClick: () => onDelete(order),
            disabled: busy || !canDelete
          })
        }
        if (isSuperAdmin) {
          actions.push({
            key: 'forceDelete',
            label: '强制删除',
            danger: true,
            disabled: busy,
            onClick: () => onForceDelete(order)
          })
        }
        return <TableRowActions resource="order.service" actions={actions} maxVisibleActions={1} />
      }
    }
  ]
}
